import { getAnalytics } from "firebase/analytics";
import { initializeApp } from "firebase/app";
import {
  getToken as _getToken,
  getMessaging,
  isSupported,
  onMessage,
  type MessagePayload,
  type Messaging,
} from "firebase/messaging";

export const VAPID_PUBLIC_KEY = import.meta.env
  .VITE_FIREBASE_VAPID_PUBLIC_KEY as string;
export const firebaseAppId = import.meta.env.VITE_FIREBASE_APP_ID as string;
export const firebaseApiKey = import.meta.env.VITE_FIREBASE_API_KEY as string;

// pluck values from your `google-services.json` file you created on the firebase console
const webConfig = {
  apiKey: firebaseApiKey,
  authDomain: "mdoc-completehealth-app.firebaseapp.com",
  databaseURL: "https://mdoc-completehealth-app.firebaseio.com",
  projectId: "mdoc-completehealth-app",
  storageBucket: "mdoc-completehealth-app.appspot.com",
  messagingSenderId: "310150579193",
  appId: firebaseAppId,
  measurementId: "G-PVHP8KQV5Q",
};

export const firebaseConfig = webConfig;

const app =
  firebaseApiKey && firebaseAppId ? initializeApp(firebaseConfig) : null;

let messagingInstance: Messaging | null = null;
let messagingInitPromise: Promise<Messaging | null> | null = null;

const getMessagingInstance = async (): Promise<Messaging | null> => {
  if (!app) {
    return null;
  }
  if (messagingInstance) {
    return messagingInstance;
  }
  messagingInitPromise ??= (async () => {
    try {
      if (!(await isSupported())) {
        return null;
      }
      messagingInstance = getMessaging(app);
      return messagingInstance;
    } catch {
      return null;
    } finally {
      // Allow retry when init fails; successful instances stay cached above.
      if (!messagingInstance) {
        messagingInitPromise = null;
      }
    }
  })();
  return messagingInitPromise;
};

if (!app)
  // eslint-disable-next-line no-console
  console.error(
    "Firebase initialization skipped: API or App Id key is missing",
  );

export const initializeFirebase = () => {
  if (!app) {
    return {
      status: false,
      analytics: null,
    };
  }

  const analytics = getAnalytics(app);

  return {
    status: true,
    analytics,
  };
};

export const getFcmToken = async (registration?: ServiceWorkerRegistration) => {
  const messaging = await getMessagingInstance();
  if (!messaging) {
    return null;
  }
  return _getToken(messaging, {
    vapidKey: VAPID_PUBLIC_KEY,
    serviceWorkerRegistration:
      registration ?? (await navigator.serviceWorker.getRegistration()),
  });
};

// Foreground message listener
export const onForegroundMessage = (
  handler: (payload: MessagePayload) => void | Promise<void>,
) => {
  let unsubscribe: (() => void) | undefined;
  let isActive = true;

  void getMessagingInstance().then((messaging) => {
    if (!messaging) {
      return;
    }
    const unsub = onMessage(messaging, (payload) => {
      void handler(payload);
    });
    if (!isActive) {
      unsub();
      return;
    }
    unsubscribe = unsub;
  });

  return () => {
    isActive = false;
    unsubscribe?.();
  };

  // Background message listener is handled by the service worker in firebase-messaging-sw.js
};
